import { useState, useMemo } from 'react';
import { Trash2, ChevronDown, ChevronUp, FileText, Camera } from 'lucide-react';
import { useEmailContext } from '../../../../../contexts/EmailContext';
import { colors } from '../../../../../data/constants';

const RealtyPreviewCard = ({ realty, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const imageUrl = useMemo(() => {
    if (!realty?.photo?.photos) return null;
    const categoryOrder = [
      'Master', 'Exterior', 'Living1', 'Kitchen1',
      'Bathroom1', 'Bedroom1', 'Plans', 'Unsorted',
    ];
    for (const category of categoryOrder) {
      const photos = realty.photo.photos[category];
      if (photos?.length > 0) return photos[0].medium_image || photos[0].image;
    }
    return null;
  }, [realty?.photo]);

  const textRu = realty.text_description?.description_text_ru;
  const textEn = realty.text_description?.description_text_en;
  const textFr = realty.text_description?.description_text_fr;
  const titleRu = realty.text_description?.description_title_ru;
  const titleEn = realty.text_description?.description_title_en;
  const titleFr = realty.text_description?.description_title_fr;
  const hasDescription = textRu || textEn || textFr;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        <span className="text-sm text-blackColor font-medium">Property ID: {realty.id}</span>
        <button
          type="button"
          onClick={() => onRemove(realty.id)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Remove property"
        >
          <Trash2 size={16} className="text-gray-600 hover:text-red-500" />
        </button>
      </div>

      <div
        className={`relative transition-all duration-300 ${isExpanded ? 'w-full sm:w-[40%]' : 'w-full xs:w-[360px]'} h-[240px] cursor-pointer group`}
        onClick={() => setIsExpanded(v => !v)}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Property"
            className="block w-full h-full object-cover object-center rounded border border-gray-200 shadow-sm group-hover:opacity-95 transition-opacity"
          />
        ) : (
          <div className="flex justify-center items-center w-full h-full bg-gray-100 rounded border border-gray-200">
            <Camera size={44} color={colors.grayColor} />
          </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded flex items-center justify-center">
          {!isExpanded && hasDescription && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <FileText size={16} className="text-gray-600" />
              <span className="text-sm text-gray-700">Click to view details</span>
            </div>
          )}
        </div>
      </div>

      {hasDescription && (
        <button
          type="button"
          onClick={() => setIsExpanded(v => !v)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          {isExpanded
            ? <><ChevronUp size={16} /><span>Hide description</span></>
            : <><ChevronDown size={16} /><span>Show description</span></>
          }
        </button>
      )}

      {isExpanded && hasDescription && (
        <div className="w-full bg-gray-50 p-4 rounded border border-gray-200 animate-in fade-in duration-200 sm:w-[40%] text-sm text-gray-600 whitespace-pre-wrap">
          {!!textRu && <><p className="text-sm font-bold">{titleRu}</p><br />{textRu}<br /></>}
          {!!textEn && <><p className="text-sm font-bold">{titleEn}</p><br />{textEn}<br /></>}
          {!!textFr && <><p className="text-sm font-bold">{titleFr}</p><br />{textFr}<br /></>}
        </div>
      )}
    </div>
  );
};

export const EmailRealtyPreview = () => {
  const { selectedRealties, removeRealty } = useEmailContext();

  if (!selectedRealties.length) return null;

  return (
    <div className="flex flex-col items-start gap-4">
      <span className="text-ml text-blackColor font-medium">Selected Properties</span>
      {selectedRealties.map(realty => (
        <RealtyPreviewCard key={realty.id} realty={realty} onRemove={removeRealty} />
      ))}
    </div>
  );
};
